import {
  Menu,
  MenuItemBasicWithSides,
  MenuItemUpdateDeepInBatch,
  MenuOptionGroupBasicWithItems,
  MenuOptionGroupUpdateDeepInBatch,
  MenuUpdateDeepInBatch,
} from "client/jspPlatformExperiment";
import {
  isMenuItemBasicWithSides,
  isMenuSectionBasicWithItems,
} from "models/menu/menu";

/**
 * Convert MenuItemBasicWithSides to MenuItemUpdateDeepInBatch
 */
export const getMenuItemUpdateDeepInBatch = (
  menuItem: MenuItemBasicWithSides,
): MenuItemUpdateDeepInBatch => {
  return {
    id: menuItem.id,
    // food_id: menuItem.food_id,
    // display_name: menuItem.display_name ?? null,
    cap: menuItem.cap,
    // portion_cost: menuItem.portion_cost ?? null,
    // portion_price: menuItem.portion_price,
    option_type: "menu item",
    side_option_groups: menuItem.side_option_groups?.map((sideGroup) =>
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      getMenuOptionGroupUpdateDeepInBatch(sideGroup),
    ),
  };
};

/**
 * Convert MenuOptionGroupBasicWithItems to MenuOptionGroupUpdateDeepInBatch
 */
export const getMenuOptionGroupUpdateDeepInBatch = (
  section: MenuOptionGroupBasicWithItems,
): MenuOptionGroupUpdateDeepInBatch => {
  return {
    id: section.id,
    name: section.name,
    option_type: "option group",
    options: section.options.map((option) => {
      if (isMenuItemBasicWithSides(option)) {
        return getMenuItemUpdateDeepInBatch(option);
      } else if (isMenuSectionBasicWithItems(option)) {
        return getMenuOptionGroupUpdateDeepInBatch(option);
      }
      // Fallback - should not happen with proper types
      return option as MenuItemUpdateDeepInBatch;
    }),
  };
};

/**
 * Convert Menu to MenuUpdateDeepInBatch for batch updating
 */
export const getMenuUpdateDeepInBatchFromMenu = (
  menu: Menu,
): MenuUpdateDeepInBatch => {
  return {
    id: menu.id,
    // name: menu.name ?? null,
    // serving_datetime: menu.serving_datetime ?? null,
    // publish_prior_to_serving_time: menu.publish_prior_to_serving_time,
    // cap_finalization_prior_to_serving_time:
    //   menu.cap_finalization_prior_to_serving_time,
    // user_group_ids: menu.user_groups.map((group) => group.id),
    sections: menu.sections.map((section) =>
      getMenuOptionGroupUpdateDeepInBatch(section),
    ),
  };
};

/**
 * Convert an array of Menu objects to MenuUpdateDeepInBatch array
 */
export const getMenusUpdateDeepInBatchFromMenus = (
  menus: Menu[],
): MenuUpdateDeepInBatch[] => {
  return menus.map((menu) => getMenuUpdateDeepInBatchFromMenu(menu));
};

/**
 * Recursively update cap values for all menu items in a section (MenuUpdateDeepInBatch format)
 * @param section - The section to update
 * @param sectionName - The name of the section to match
 * @param newCap - The new cap value to set
 * @param itemIndex - Optional index of the item to update (0-based). If null, updates all items.
 */
export const updateSectionCapValuesInBatch = (
  section: MenuOptionGroupUpdateDeepInBatch,
  sectionName: string,
  newCap: number,
  itemIndex: number | null = null,
): MenuOptionGroupUpdateDeepInBatch => {
  // Update items in this section
  const updatedOptions = section.options?.map((option, index) => {
    // If itemIndex is specified and doesn't match, skip this option
    if (itemIndex !== null && index !== itemIndex) {
      return option;
    }

    if ("cap" in option && "id" in option) {
      // This is a MenuItemUpdateDeepInBatch - update its cap
      const menuItem = option;
      // Also check if it has side_option_groups that match the section name
      const updatedSideOptionGroups = menuItem.side_option_groups?.map(
        (sideGroup) => {
          // Only process MenuOptionGroupUpdateDeepInBatch (has id)
          if ("id" in sideGroup) {
            return updateSectionCapValuesInBatch(
              sideGroup,
              sectionName,
              newCap,
            );
          }
          return sideGroup;
        },
      );
      return {
        ...menuItem,
        cap: newCap,
        ...(updatedSideOptionGroups && {
          side_option_groups: updatedSideOptionGroups,
        }),
      } as MenuItemUpdateDeepInBatch;
    } else if (
      "option_type" in option &&
      option.option_type === "option group" &&
      "id" in option
    ) {
      // This is a nested MenuOptionGroupUpdateDeepInBatch - recurse
      return updateSectionCapValuesInBatch(option, sectionName, newCap);
    }
    return option;
  });
  return {
    ...section,
    options: updatedOptions,
  };
};
